import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Topbar from "../components/layout/Topbar.jsx";
import { getCompany, getCompanyUsers, updateCompany } from "../services/companyService.js";

/**
 * Fiche détaillée d'une entreprise cliente.
 *
 * Onglets :
 *  - Vue d'ensemble : infos de contact, statut
 *  - Utilisateurs   : liste réelle des users rattachés (GET /companies/{id}/users)
 *
 * Les onglets Tickets et Notes restent à brancher plus tard
 * (pas encore d'endpoint backend dédié pour ces données).
 */
export default function ClientDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("overview");

  // ── Données ──────────────────────────────────
  const [company, setCompany] = useState(null);
  const [users,   setUsers]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");

  // ── Édition inline du statut actif/inactif ──
  const [togglingActive, setTogglingActive] = useState(false);

  useEffect(() => { loadData(); }, [id]);

  const loadData = async () => {
    setLoading(true);
    setError("");
    try {
      // Chargement en parallèle de l'entreprise et de ses utilisateurs
      const [companyRes, usersRes] = await Promise.all([
        getCompany(id),
        getCompanyUsers(id),
      ]);
      setCompany(companyRes.data);
      setUsers(usersRes.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Active/désactive l'entreprise depuis le header
  const handleToggleActive = async () => {
    if (!company) return;
    setTogglingActive(true);
    try {
      const res = await updateCompany(company.id, { ...company, active: !company.active });
      setCompany(res.data);
    } catch (err) {
      alert("Erreur : " + err.message);
    } finally {
      setTogglingActive(false);
    }
  };

  // Formate une date ISO
  const fmtDate = (iso) => iso
    ? new Date(iso).toLocaleDateString("fr-FR", { month: "long", year: "numeric" })
    : "—";

  // ── Chargement ──────────────────────────────
  if (loading) {
    return (
      <>
        <Topbar title="Chargement…"/>
        <div className="page-body">
          <div style={{ textAlign: "center", padding: 80, color: "var(--text-3)" }}>
            <i className="bi bi-arrow-repeat spin" style={{ fontSize: 32, opacity: .4 }}/>
            <p style={{ marginTop: 14 }}>Chargement de la fiche client…</p>
          </div>
        </div>
      </>
    );
  }

  // ── Erreur ──────────────────────────────────
  if (error || !company) {
    return (
      <>
        <Topbar title="Erreur"/>
        <div className="page-body">
          <div style={{ textAlign: "center", padding: 80 }}>
            <i className="bi bi-exclamation-triangle" style={{ fontSize: 32, color: "var(--danger)" }}/>
            <p style={{ color: "var(--danger)", marginTop: 14 }}>{error || "Entreprise introuvable"}</p>
            <button className="btn-itm btn-itm-outline" onClick={() => navigate("/clients")} style={{ marginTop: 14 }}>
              <i className="bi bi-arrow-left"/> Retour à la liste
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Topbar title={company.name}/>
      <div className="page-body">

        {/* Breadcrumb */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 20, fontSize: 13, color: "var(--text-3)" }}>
          <span style={{ cursor: "pointer", color: "var(--accent)" }} onClick={() => navigate("/clients")}>
            <i className="bi bi-arrow-left me-1" />Clients
          </span>
          <i className="bi bi-chevron-right" style={{ fontSize: 10 }} />
          <span>{company.name}</span>
        </div>

        {/* Header entreprise */}
        <div className="itm-card anim-fade-up" style={{ marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <div style={{ width: 56, height: 56, borderRadius: 14, background: "var(--accent-dim)", color: "var(--accent)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, fontWeight: 800, fontFamily: "Syne,sans-serif", flexShrink: 0 }}>
                {company.name?.[0]}
              </div>
              <div>
                <h2 style={{ fontFamily: "Syne,sans-serif", fontSize: 20, fontWeight: 800, color: "var(--text-1)", marginBottom: 4 }}>
                  {company.name}
                </h2>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  <span className={`badge-status ${company.active ? "bs-active" : "bs-inactive"}`}>
                    {company.active ? "Actif" : "Inactif"}
                  </span>
                  {company.city && (
                    <span style={{ fontSize: 12, color: "var(--text-2)" }}>
                      <i className="bi bi-geo-alt me-1" />{company.city}{company.country ? `, ${company.country}` : ""}
                    </span>
                  )}
                  <span style={{ fontSize: 12, color: "var(--text-2)" }}>
                    <i className="bi bi-calendar me-1" />Depuis {fmtDate(company.createdAt)}
                  </span>
                  {company.ateraCustomerId && (
                    <span style={{ fontSize: 12, color: "var(--text-2)" }}>
                      <i className="bi bi-cloud me-1" />Atera #{company.ateraCustomerId}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div style={{ display: "flex", gap: 8 }}>
              {/* Toggle actif/inactif */}
              <button
                className="btn-itm btn-itm-outline"
                onClick={handleToggleActive}
                disabled={togglingActive}
                style={{ color: company.active ? "var(--danger)" : "var(--success)" }}
              >
                {togglingActive
                  ? <i className="bi bi-arrow-repeat spin"/>
                  : <i className={`bi ${company.active ? "bi-slash-circle" : "bi-check-circle"}`}/>
                }
                {company.active ? "Désactiver" : "Activer"}
              </button>
              <button className="btn-itm btn-itm-primary" onClick={() => navigate(`/tickets?companyId=${id}`)}>
                <i className="bi bi-ticket-detailed" /> Voir les tickets
              </button>
            </div>
          </div>
        </div>

        {/* Onglets */}
        <div className="itm-tabs">
          {[
            { id: "overview", label: "Vue d'ensemble" },
            { id: "users",    label: "Utilisateurs", count: users.length },
            { id: "security", label: "Sécurité" },
          ].map(t => (
            <div key={t.id} className={`itm-tab ${activeTab === t.id ? "active" : ""}`} onClick={() => setActiveTab(t.id)}>
              {t.label}
              {t.count !== undefined && (
                <span style={{ marginLeft: 6, background: "var(--navy-soft)", borderRadius: 20, padding: "1px 7px", fontSize: 11, color: "var(--text-2)" }}>
                  {t.count}
                </span>
              )}
            </div>
          ))}
        </div>

        {/* Tab Vue d'ensemble */}
        {activeTab === "overview" && (
          <div className="anim-fade-in">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>

              {/* Coordonnées */}
              <div className="itm-card anim-fade-up">
                <div className="card-title" style={{ marginBottom: 14 }}>Coordonnées</div>
                {[
                  ["Email",          company.email],
                  ["Téléphone",      company.phone],
                  ["Adresse",        company.address],
                  ["Contact",        company.contactName],
                  ["Email contact",  company.contactEmail],
                ].filter(([, v]) => v).map(([k, v]) => (
                  <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid var(--border)", alignItems: "center" }}>
                    <span style={{ fontSize: 12, color: "var(--text-3)" }}>{k}</span>
                    <span style={{ fontSize: 13, color: "var(--text-2)", textAlign: "right", maxWidth: "60%" }}>{v}</span>
                  </div>
                ))}
                {!company.email && !company.phone && !company.address && (
                  <p style={{ fontSize: 12, color: "var(--text-3)" }}>Aucune coordonnée renseignée.</p>
                )}
              </div>

              {/* Synchronisation Atera */}
              <div className="itm-card anim-fade-up delay-1">
                <div className="card-title" style={{ marginBottom: 14 }}>Synchronisation Atera</div>
                {company.ateraCustomerId ? (
                  <>
                    {[
                      ["ID Atera",        `#${company.ateraCustomerId}`],
                      ["Ville",           company.city],
                      ["Pays",            company.country],
                      ["Dernière sync",   company.lastSyncedAt ? fmtDate(company.lastSyncedAt) : "Jamais"],
                    ].filter(([, v]) => v).map(([k, v]) => (
                      <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid var(--border)", alignItems: "center" }}>
                        <span style={{ fontSize: 12, color: "var(--text-3)" }}>{k}</span>
                        <span style={{ fontSize: 13, color: "var(--text-2)" }}>{v}</span>
                      </div>
                    ))}
                  </>
                ) : (
                  <p style={{ fontSize: 12, color: "var(--text-3)" }}>
                    <i className="bi bi-info-circle me-2"/>Cette entreprise n'est pas synchronisée avec Atera.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Tab Utilisateurs */}
        {activeTab === "users" && (
          <div className="anim-fade-in">
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 14 }}>
              <button className="btn-itm btn-itm-primary" onClick={() => navigate(`/users?companyId=${id}`)}>
                <i className="bi bi-person-plus" /> Gérer les utilisateurs
              </button>
            </div>

            <div className="itm-card">
              {users.length === 0 ? (
                <div style={{ textAlign: "center", padding: 40, color: "var(--text-3)" }}>
                  <i className="bi bi-person-x" style={{ fontSize: 28, opacity: .4 }}/>
                  <p style={{ marginTop: 12 }}>Aucun utilisateur pour cette entreprise.</p>
                </div>
              ) : (
                <table className="itm-table">
                  <thead>
                    <tr>
                      <th>Utilisateur</th>
                      <th>Rôle</th>
                      <th>Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(u => (
                      <tr key={u.id}>
                        <td>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <div style={{ width: 32, height: 32, borderRadius: "50%", background: "linear-gradient(135deg,var(--accent3),var(--accent))", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
                              {u.firstName?.[0]}{u.lastName?.[0]}
                            </div>
                            <div>
                              <div className="cell-primary">{u.firstName} {u.lastName}</div>
                              <div className="cell-sub">{u.email}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className="badge-status bs-active">{u.role?.name || "—"}</span>
                        </td>
                        <td>
                          <span className={`badge-status ${u.active ? "bs-active" : "bs-inactive"}`}>
                            {u.active ? "Actif" : "Inactif"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* Tab Sécurité */}
        {activeTab === "security" && (
          <div className="anim-fade-in">
            <div className="itm-card anim-fade-up">
              <div className="card-title" style={{ marginBottom: 4 }}>Coffre de secours hors-ligne</div>
              <div style={{ fontSize: 12, color: "var(--text-3)", maxWidth: 560, lineHeight: 1.6, marginBottom: 18 }}>
                Le téléchargement manuel (formats .dcv ou .xlsx protégé, une ou plusieurs
                entreprises) et la génération automatique nocturne se gèrent maintenant depuis
                une page dédiée, commune à toutes les entreprises.
              </div>
              <button
                className="btn-itm btn-itm-primary"
                onClick={() => navigate(`/vault-export?companyId=${id}`)}
              >
                <i className="bi bi-shield-lock me-1" />Gérer le coffre de secours de {company.name}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}